/**
 * Per-call scheduler lane for proxied (WebSocket) calls (BI-982).
 *
 * The lane is generated in the browser and sent on `CreateCallRequest.lane`,
 * then echoed back on ClaimCall/AckCall so the worker poll/ack bind to the same
 * lane and the call routes back to this browser.
 *
 * SECURITY-CRITICAL: the lane is the cross-user isolation key. A poll on a
 * shared per-workflow agent id would otherwise hand one user's call to another
 * user's browser, so each call MUST get a distinct lane. The server enforces a
 * non-empty, well-formed lane (`validateProxiedLane`) and verifies call/case
 * ownership (IDOR guard) — so the property we need here is *uniqueness*, not
 * unguessability.
 *
 * The shape is `<userId>-<8 hex>`: the user id scopes + gives traceability, and
 * the 8-hex (32-bit, ~4.3B) random suffix makes the lane unique across a user's
 * concurrent in-flight calls (a per-user collision would need two simultaneous
 * calls to draw the same suffix — vanishingly unlikely; on collision the second
 * claim simply contends on the same lane, it does not cross users).
 *
 * It mirrors the call-scheduler's `ValidateLane` constraints: at most 32 bytes,
 * and free of ':' and whitespace.
 */

/** Mirrors the call-scheduler's ValidateLane cap, measured in UTF-8 bytes. */
const MAX_LANE_LEN = 32;

/**
 * 8 lowercase-hex chars of randomness. Prefers crypto.randomUUID, but that
 * throws outside a secure context (plain http:// on a non-localhost origin), so
 * it falls back to crypto.getRandomValues (no secure-context requirement) and
 * finally Math.random — the lane only needs per-call uniqueness, not crypto
 * strength.
 */
function randomSuffix(): string {
  const c = globalThis.crypto as Crypto | undefined;
  try {
    if (c?.randomUUID) {
      return c.randomUUID().slice(0, 8);
    }
  } catch {
    // randomUUID unavailable outside a secure context — fall through.
  }
  if (c?.getRandomValues) {
    const bytes = c.getRandomValues(new Uint8Array(4));
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  }
  return Math.floor(Math.random() * 0x1_0000_0000)
    .toString(16)
    .padStart(8, '0');
}

/** UTF-8 byte length, matching the scheduler's byte-based cap. */
function byteLength(s: string): number {
  return new TextEncoder().encode(s).length;
}

/** Trim a string to at most maxBytes UTF-8 bytes without splitting a code point. */
function truncateToBytes(s: string, maxBytes: number): string {
  if (byteLength(s) <= maxBytes) {
    return s;
  }
  let out = '';
  for (const ch of s) {
    if (byteLength(out + ch) > maxBytes) {
      break;
    }
    out += ch;
  }
  return out;
}

export function buildProxiedLane(userId?: string): string {
  // 8 hex chars: ample uniqueness for concurrent in-flight calls, and leaves
  // room for the user id within the 32-byte cap.
  const suffix = randomSuffix();
  // Strip anything the scheduler rejects (':' / whitespace) from the user id.
  const safeUserId = (userId ?? '').replace(/[:\s]/g, '');
  if (!safeUserId) {
    return suffix;
  }
  // Reserve room for "-" + suffix; trim the user id (by bytes) so the lane fits.
  const trimmed = truncateToBytes(safeUserId, MAX_LANE_LEN - suffix.length - 1);
  return trimmed ? `${trimmed}-${suffix}` : suffix;
}
